import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, CheckCircle2, Sparkles, BadgeCheck, Check } from "lucide-react";
import { PRODUCTS } from "@/data/products";
import { getOffering } from "@/data/catalog";
import { PRODUCT_EXTRAS, MOCKUP_CAPTIONS } from "@/data/productExtras";
import { INDUSTRIES } from "@/data/industries";
import { BRAND } from "@/data/site";
import DemoFormDialog from "@/components/DemoFormDialog";
import ChatMockup from "@/components/ChatMockup";
import FaqAccordion from "@/components/FaqAccordion";
import FinalCta from "@/components/FinalCta";
import Breadcrumbs from "@/components/Breadcrumbs";
import WorkflowDiagram from "@/components/WorkflowDiagram";
import InlineCta from "@/components/InlineCta";
import { MOCKUPS } from "@/components/ProductMockups";
import { SectionHeading, StepsRow, WhyWhatsApp } from "@/components/Sections";
import { useSeo, FaqSchema } from "@/lib/seo";

export default function ProductPage() {
  const { slug } = useParams();
  const p = PRODUCTS[slug];
  const meta = getOffering(slug);
  const x = PRODUCT_EXTRAS[slug] || {};

  useSeo({
    title: p ? x.seoTitle || `${p.title} | GrabNChat` : "Page not found | GrabNChat",
    description: p ? x.seoDescription || p.sub : "This page doesn't exist.",
    noindex: !p,
  });

  if (!p) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <h1 className="font-display text-3xl font-bold">Product not found</h1>
        <p className="text-slate-600 mt-2">The product you are looking for doesn’t exist.</p>
        <Link to="/features" className="inline-block mt-6 text-[#25D366] font-semibold">See all features →</Link>
      </div>
    );
  }

  const Icon = meta?.icon || Sparkles;
  const name = p.title;
  const short = x.short || name;
  const mockups = (x.mockups || []).filter((k) => MOCKUPS[k]);
  const HeroVisual = mockups.length ? MOCKUPS[mockups[0]] : ChatMockup;
  const related = (x.related || []).map(getOffering).filter(Boolean);
  const industries = (x.industries || []).filter((s) => INDUSTRIES[s]);

  return (
    <div data-testid={`product-page-${slug}`}>
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-b from-slate-50 to-white">
        <div className="absolute inset-0 bg-dots opacity-50" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 md:pt-10 pb-16">
          <Breadcrumbs items={[{ name: "Features", path: "/features" }, { name, path: `/products/${slug}` }]} />
          <div className="mt-8 md:mt-12 grid lg:grid-cols-2 gap-12 items-center">
            <div className="min-w-0">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-slate-200 bg-white text-xs font-medium text-slate-700 mb-6">
                <Icon className="w-3.5 h-3.5 text-[#25D366]" /> {name}
              </div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900">{p.hero}</h1>
              <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl">{p.sub}</p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <a href={`${BRAND.appUrl}/login`}>
                  <Button className="w-full bg-[#25D366] hover:bg-[#1DA851] text-white rounded-full px-7 h-12 font-semibold" data-testid="product-hero-start-btn">
                    Start Free Trial <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </a>
                <DemoFormDialog source={slug} defaultMessage={`I'd like to learn more about ${name}.`} trigger={
                  <Button variant="outline" className="bg-white border-slate-200 hover:border-slate-300 text-slate-900 rounded-full px-7 h-12 font-semibold" data-testid="product-hero-demo-btn">
                    Book Free Demo
                  </Button>
                } />
              </div>
              <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-slate-600">
                <span className="flex items-center gap-2"><BadgeCheck className="w-4 h-4 text-[#25D366]" /> Official WhatsApp Business API</span>
                <a href="#how-it-works" className="font-semibold text-slate-800 hover:text-[#25D366]">See how it works ↓</a>
              </div>
            </div>
            <div className="relative min-w-0"><HeroVisual /></div>
          </div>
        </div>
      </section>


      {/* 1. What is it + 4. How it helps */}
      {meta?.whatIs && (
        <section className="py-20 md:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7 min-w-0">
              <SectionHeading eyebrow="Overview" title={`What is ${name}?`} />
              <p className="mt-6 text-lg text-slate-700 leading-relaxed">{meta.whatIs}</p>
            </div>
            <div className="lg:col-span-5 min-w-0 bg-slate-50 border border-slate-200 rounded-3xl p-7">
              <h2 className="font-display text-xl font-semibold text-slate-900">How {short} helps your business</h2>
              <ul className="mt-5 space-y-3">
                {p.benefits.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-slate-800" data-testid="product-benefit">
                    <CheckCircle2 className="w-5 h-5 text-[#25D366] mt-0.5 shrink-0" /> <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      )}

      {/* 2. How it works */}
      {meta?.steps && (
        <section id="how-it-works" className="py-20 md:py-24 bg-slate-50 scroll-mt-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading eyebrow="How it works" title={`How ${short} works`} sub="Get started in three simple steps." />
            <div className="mt-12"><StepsRow steps={meta.steps} /></div>
          </div>
        </section>
      )}

      {/* 3. Key features */}
      <section className="py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Features" title={`Key features of ${name}`} />
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {p.features.map((f, i) => (
              <div key={f.title} className="min-w-0 bg-white border border-slate-200 rounded-2xl p-6 hover:shadow-md transition-shadow" data-testid={`product-feature-${i}`}>
                <span className="w-10 h-10 rounded-xl grid place-items-center bg-[#25D366]/10 text-[#25D366]"><Check className="w-5 h-5" /></span>
                <h3 className="mt-4 font-display text-lg font-semibold text-slate-900">{f.title}</h3>
                <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
          <InlineCta source={slug} text={`Try ${short} free for 7 days`} secondary={{ to: "/pricing", label: "See pricing" }} />
        </div>
      </section>

      {/* 5. Real workflow */}
      {x.workflow?.length > 0 && (
        <section className="py-20 md:py-24 bg-[#0F172A] relative overflow-hidden" data-testid="product-workflow">
          <div className="absolute inset-0 bg-dots opacity-20" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading dark center eyebrow="Real workflow" title={`The ${short} workflow, step by step`} sub="What actually happens inside GrabNChat, from the first step to the result." />
            <div className="mt-12"><WorkflowDiagram dark steps={x.workflow} /></div>
          </div>
        </section>
      )}

      {/* 6. Product screenshots (code-drawn dashboard previews) */}
      {mockups.length > 0 && (
        <section className="py-20 md:py-24" data-testid="product-gallery">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading eyebrow="Product tour" title={`See ${short} in the GrabNChat dashboard`} />
            <div className={`mt-12 grid gap-10 ${mockups.length > 1 ? "lg:grid-cols-2" : "max-w-2xl"}`}>
              {mockups.map((k) => {
                const M = MOCKUPS[k];
                const [t, d] = MOCKUP_CAPTIONS[k] || [];
                return (
                  <div key={k} className="min-w-0">
                    <M />
                    <h3 className="mt-6 font-display text-lg font-semibold text-slate-900">{t}</h3>
                    <p className="mt-1 text-sm text-slate-600 leading-relaxed">{d}</p>
                  </div>
                );
              })}
            </div>
            <p className="mt-6 text-xs text-slate-400">Previews use sample data.</p>
          </div>
        </section>
      )}

      {/* 7. Use cases */}
      <section className={`py-20 md:py-24 ${mockups.length ? "bg-slate-50" : ""}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Use cases" title={`What can you do with ${short}?`} />
          <ul className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4">
            {p.useCases.map((u, i) => (
              <li key={u} className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl p-4 text-sm font-medium text-slate-800" data-testid={`product-usecase-${i}`}>
                <span className="w-2 h-2 rounded-full bg-[#25D366] shrink-0" /> {u}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Industries + related features (internal links) */}
      {(industries.length > 0 || related.length > 0) && (
        <section className="py-20 md:py-24" data-testid="product-related">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12">
            {industries.length > 0 && (
              <div className="min-w-0">
                <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">{short} by industry</h2>
                <ul className="mt-6 grid sm:grid-cols-2 gap-3">
                  {industries.map((s) => (
                    <li key={s}>
                      <Link to={`/industries/${s}`} className="group flex h-full items-center justify-between gap-2 rounded-xl border border-slate-200 bg-white p-4 hover:border-[#25D366]">
                        <span className="text-sm font-semibold text-slate-800">{INDUSTRIES[s].title}</span>
                        <ArrowRight className="w-4 h-4 text-[#25D366] group-hover:translate-x-0.5 transition-transform" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {related.length > 0 && (
              <div className="min-w-0">
                <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">Related features</h2>
                <ul className="mt-6 grid sm:grid-cols-2 gap-3">
                  {related.map((o) => (
                    <li key={o.slug}>
                      <Link to={`/products/${o.slug}`} className="group flex h-full items-start gap-3 rounded-xl border border-slate-200 bg-white p-4 hover:border-[#25D366]">
                        <span className="w-9 h-9 shrink-0 rounded-lg grid place-items-center bg-[#25D366]/10 text-[#25D366]"><o.icon className="w-4 h-4" /></span>
                        <span className="min-w-0">
                          <span className="block text-sm font-semibold text-slate-900">{o.title}</span>
                          <span className="block text-xs text-slate-500 mt-0.5">{o.short}</span>
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
                <Link to="/features" className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-[#25D366]">Explore all features <ArrowRight className="w-4 h-4" /></Link>
              </div>
            )}
          </div>
        </section>
      )}

      {/* 8. Benefits of the channel */}
      <WhyWhatsApp />

      {/* 9. FAQs */}
      {p.faqs?.length > 0 && (
        <section className="py-20 md:py-24">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 text-center">Frequently asked questions about {name}</h2>
            <div className="mt-10"><FaqAccordion faqs={p.faqs} /></div>
            <FaqSchema faqs={p.faqs} />
          </div>
        </section>
      )}

      <FinalCta title="Start Growing with GrabNChat" sub={`Try ${short} free for 7 days, or book a demo and we'll set it up with you.`} ctaSource={slug} />
    </div>
  );
}
